import React, { useEffect, useState } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
  tooltipClasses,
  CircularProgress,
  Badge,
} from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { all_routes } from "../../../router/all_routes";
import ActionsDropdown from "../../../../core/common/crmComponents/ActionsDropdown";
import {
  MODULES,
  MODULES_ACTIONS,
} from "../../../../core/data/constants/moduleConstants";
import LeadApi from "../api/LeadApi";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import HasPermission from "../../../../core/common/wrapper/HasPermission";
import {
  CALLER,
  COMPANY_ADMIN,
  SUPER_ADMIN,
  TEAM_MANAGER,
} from "../../../../core/data/constants/constant";
import { useCallHandler } from "../../../../utils/hooks/useCallHandler";
import { getAllUrlParams } from "../../../../utils/helpers/setAndGetUrlParams";
import {
  convertSecondsToHMS,
  convertToUTCString,
} from "../../../../utils/helpers/helper";
import { useUsersByRole } from "../../manageUsers/hooks/useUserByRole";
import { setFilterModel } from "../../../../core/data/redux/filterSlice";
import { customMuiDateFilter } from "../../../../utils/helpers/customMuiDateFilter";
import { HiDuplicate } from "react-icons/hi";
import { formatLeadsData } from "../../../../utils/helpers/formatModulesData";
import SocialMediaIcons from "../../../../core/common/crmComponents/SocialMediaIcons";
import { FiClock, FiSettings } from "react-icons/fi";
import useReminders from "../../reminders/hooks/useReminders";
import { FaCheck, FaCopy, FaMinus } from "react-icons/fa";
import ZoomMeetingStatus from "../../../../core/common/crmComponents/zoomMeeting/ZoomMeetingStatus";
import { useFunctionalityAccess } from "../../../../core/common/wrapper/HasFunctionalityAccess";
import ShareLead from "./ShareLead";
import PostCallModal from "./PostCallModal";
import { MdOutlineContentCopy } from "react-icons/md";

const LeadList = ({ leadsData, selectedCompany, setFilteredData }) => {
  const route = all_routes;
  const { user } = useSelector((state) => state?.auth);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [isOpenPostCallModal, setIsOpenPostCallModal] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [shareData, setShareData] = useState({});
  const [hasMore, setHasMore] = useState(true);
  const [limit, setLimit] = useState(10);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const { mutate, isPending, isSuccess } = useCallHandler(
    MODULES.LEADS,
    selectedLeadId,
  );
  const hasShareLead = useFunctionalityAccess("shareLeads");
  const openModal = () => setIsOpenPostCallModal(true);
  const closeModal = () => setIsOpenPostCallModal(false);

  const { reminders } = useReminders();

  const filterModel = useSelector((state) => state.filter.filterModel);
  const dispatch = useDispatch();

  const allParams = getAllUrlParams(); // Get all URL params
  const queryString = new URLSearchParams(allParams).toString();
  const canChangeLeadOwner = useFunctionalityAccess("changeLeadOwnerShip");
  const hasZoomAccess = useFunctionalityAccess("zoomSetting");
  const hasCallPermission = useFunctionalityAccess("CallPermission");

  // Function to check if a lead has a reminder
  const hasReminder = (leadId) => {
    return reminders?.some((reminder) => reminder.leadId === leadId);
  };

  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useUsersByRole({
    role: "caller",
    page: 1,
    limit: 50,
    ofCompany: selectedCompany,
    status: "active",
    enable: isDialogOpen,
  });

  useEffect(() => {
    if (data?.data) {
      setUsers((prev) => {
        // Use a Map to remove duplicates based on _id
        const uniqueUsers = new Map(prev.map((user) => [user._id, user]));
        data.data.forEach((user) => uniqueUsers.set(user._id, user));

        return Array.from(uniqueUsers.values());
      });

      setHasMore(data?.data?.length >= limit); // Check if more data is available
    }
  }, [data]);

  const handleSelectionChange = (id, callerid) => {
    setSelectedLeadIds((prev) =>
      prev.some((el) => el.id === id)
        ? prev.filter((el) => el.id !== id)
        : [...prev, { id, callerid }],
    );
  };

  const openChangeOwnerDialog = () => {
    setIsDialogOpen(true);
    setPage(1);
    // setUsers([]);
  };

  const changeLeadOwner = async () => {
    try {
      const leadIds = selectedLeadIds
        .filter((el) => el.callerid !== selectedUserId)
        .map((el) => el.id);

      if (leadIds.length === 0) {
        toast.warning("Selected leads already belong to this caller.");
        setIsDialogOpen(false);
        setPage(1);
        setHasMore(true);
        setSelectedUserId(null);
        setSelectedLeadIds([]);
        return;
      }
      if (selectedLeadIds.length > leadIds.length) {
        toast.warning(
          "Some selected leads belong to the same caller and won't be updated.",
        );
      }

      const res = await LeadApi.changleLeadOwner(leadIds, selectedUserId);
      toast.success(res.msg || "Leads reassigned successfully.");
      queryClient.invalidateQueries("lead");

      setIsDialogOpen(false);
    } catch (error) {
      toast.error(error.response.data?.msg || "Failed to change lead owner.");
    } finally {
      setPage(1);
      setHasMore(true);
      setSelectedUserId(null);
      setSelectedLeadIds([]);
    }
  };

  // Flatten the leadsData array
  const processedRows = formatLeadsData(leadsData);

  const columns = [
    {
      field: "actions",
      headerName: "Actions",
      width: 65,
      renderCell: (params) => (
        <ActionsDropdown
          params={params}
          module={MODULES.LEADS}
          path={route.leadsDetails}
        >
          {hasShareLead && (
            <button
              // to={`${route.leadsDetails.replace(":id", params.row.id)}?${queryString}`}
              onClick={() => {
                setShareDialogOpen(true);
                console.log("data", params);
                setShareData(params.row);
              }}
              className="dropdown-item  "
            >
              <i className="ti  text-blue pe-2 ti-share" /> Share Lead
            </button>
          )}
        </ActionsDropdown>
      ),
    },
    ...(canChangeLeadOwner
      ? [
          {
            field: "id",
            headerName: "ID",
            width: 50,
            renderCell: (params) => (
              <HasPermission
                module={MODULES.USERS}
                action={MODULES_ACTIONS.UPDATE}
              >
                <input
                  type="checkbox"
                  checked={selectedLeadIds.some(
                    (el) => el.id === params.row.id,
                  )}
                  onChange={() =>
                    handleSelectionChange(
                      params.row.id,
                      params.row.assignedToId,
                    )
                  }
                />
              </HasPermission>
            ),
            renderHeader: () => (
              <Tooltip title="ID" arrow>
                <span>ID</span>
              </Tooltip>
            ),
          },
        ]
      : []),
    {
      field: "createdAt",
      headerName: "Created At",
      width: 210,
      valueFormatter: (params) =>
        params?.value ? convertToUTCString(params?.value) : "",
      filterOperators: customMuiDateFilter,
      renderCell: (params) => (
        <div>
          {params?.row?.createdTime
            ? convertToUTCString(params?.row?.createdTime)
            : convertToUTCString(params?.row?.lastActivityDate)}
        </div>
      ),
    },
    {
      field: "name",
      headerName: "Client Name",
      width: 180,
      renderCell: (params) => {
        const leadDetailsUrl = `${route.leadsDetails.replace(
          ":id",
          params.row.id,
        )}?${queryString}`;

        return (
          <div className="d-flex align-items-center">
            {params.row?.isDuplicate && (
              <Tooltip
                title={`This lead has ${params.row.duplicateCount} duplicate(s)`}
                arrow
              >
                <span className="d-flex align-items-center me-3">
                  <Badge
                    badgeContent={params.row.duplicateCount}
                    classes={{
                      badge: "bg-warning-subtle text-dark fw-bold",
                    }}
                  >
                    <HiDuplicate
                      className="text-warning"
                      style={{ fontSize: "1.2rem" }}
                      size={25}
                    />
                  </Badge>
                </span>
              </Tooltip>
            )}
            {hasReminder(params.row.id) && (
              <FiClock
                className="me-2 flex-shrink-0"
                size={18}
                style={{
                  color: "#ff9900",
                }}
              />
            )}
            <Link
              className="text-capitalize list-link text-truncate"
              to={leadDetailsUrl}
              style={{
                maxWidth: "250px", // Adjust width as needed
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {params.row.firstName} {params.row.lastName}
            </Link>
          </div>
        );
      },
    },
    {
      field: "mobile",
      headerName: "Phone",
      width: 160,
      renderCell: (params) => (
        <div className="d-flex justify-content-start align-items-center">
          {hasCallPermission && user?.agentId && user?.campaignName ? (
            <Tooltip
              title="Click To Call"
              placement="bottom"
              slotProps={{
                popper: {
                  sx: {
                    [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
                      {
                        marginTop: "0px", // Adjust this to reduce space
                        transform: "translateY(0px)",
                      },
                  },
                },
              }}
            >
              <button
                className="d-flex align-items-center nav-link border-0 bg-transparent"
                onClick={() => {
                  if (!isPending) {
                    setSelectedLeadId(params.id);
                    mutate(params?.row?.mobile);
                    openModal();
                  }
                  navigator.clipboard
                    .writeText(`${params?.row?.mobile}`)
                    .then(() => {
                      toast.success("Copied Link To Clipborad");
                    })
                    .catch((err) => {
                      toast.error("Failed To Copy Link");
                    });
                }}
                disabled={isPending}
              >
                <div className="p-0 flex-shrink-0 text-primary me-2 fs-5">
                  {isPending && selectedLeadId === params.id ? (
                    <i className="ti ti-loader spinner" />
                  ) : (
                    <i className="ti ti-phone" />
                  )}
                </div>
                <p className="mb-0">
                  ****{params?.row?.mobile?.slice(-4) || "N/A"}{" "}
                </p>
              </button>
            </Tooltip>
          ) : hasCallPermission ? (
            <a
              onClick={(e) => {
                e.preventDefault();
                navigator.clipboard
                  .writeText(`${params?.row?.mobile}`)
                  .then(() => {
                    toast.success("Copied Link To Clipborad");
                  })
                  .catch((err) => {
                    toast.error("Failed To Copy Link");
                  });
                setSelectedLeadId(params.id);
                openModal();
                setTimeout(() => {
                  window.location.href = `tel:${params?.row?.mobile}`;
                }, 500); // Delay to allow modal to open or animate
              }}
              className="d-flex align-items-center nav-link border-0 bg-transparent "
            >
              <div
                className="p-0 flex-shrink-0 text-primary me-2 fs-5 cursor-pointer"
                onClick={() => {
                  navigator.clipboard
                    .writeText(`${params?.row?.mobile}`)
                    .then(() => {
                      toast.success("Copied Link To Clipborad");
                    })
                    .catch((err) => {
                      toast.error("Failed To Copy Link");
                    });
                  openModal();
                }}
              >
                <i className="ti ti-phone" />
              </div>
              <p className="mb-0 ">
                ****{params?.row?.mobile.slice(-4) || "N/A"}{" "}
              </p>
            </a>
          ) : (
            <p className="mb-0 ">
              ****{params?.row?.mobile.slice(-4) || "N/A"}{" "}
            </p>
          )}
          <button
            className="btn btn-sm btn-light ms-1"
            onClick={() => {
              navigator.clipboard
                .writeText(`${params?.row?.mobile}`)
                .then(() => {
                  toast.success("Number Copied to Clipborad");
                })
                .catch((err) => {
                  toast.error("Failed To Copy Number");
                });
            }}
          >
            <MdOutlineContentCopy size={18} />
          </button>
        </div>
      ),
    },
    {
      field: "",
      headerName: "Social Media",
      width: 100,
      sortable: true,
      renderCell: (params) => {
        return (
          <div className="d-flex justify-content-start align-items-center gap-2">
            <SocialMediaIcons
              platform="whatsapp"
              phoneNumber={`${params?.row?.mobile}`}
            />

            {hasZoomAccess && <ZoomMeetingStatus params={params} />}
          </div>
        );
      },
    },
    {
      field: "currentStatus",
      headerName: "Status",
      width: 110,
      sortable: true,
      renderCell: (params) => {
        return (
          <span
            className={`badge badge-pill badge-status text-capitalize`}
            style={{ backgroundColor: `${params?.row?.currentStatusColor}` }}
          >
            {params.value}
          </span>
        );
      },
    },
    {
      field: "description",
      headerName: "Description",
      width: 200,
      renderCell: (params) => (
        <div className="text-capitalize">
          {params?.row?.description || "N/A"}
        </div>
      ),
    },
    {
      field: "city",
      headerName: "City",
      width: 130,
      renderCell: (params) => (
        <div className="text-capitalize">
          {params?.row?.city || params?.row?.address?.city || "N/A"}
        </div>
      ),
    },
    {
      field: "productName",
      headerName: "Product Name",
      width: 130,
      renderCell: (params) => (
        <div className="text-capitalize">
          {params?.row?.productName || "N/A"}
        </div>
      ),
    },
    {
      field: "language",
      headerName: "Language",
      width: 130,
      renderCell: (params) => (
        <div className="text-capitalize">{params?.row?.language || "N/A"}</div>
      ),
    },
    {
      field: "leadSource",
      headerName: "Lead Source",
      width: 130,
      renderCell: (params) => (
        <div className="text-capitalize">
          {params?.row?.leadSource || "N/A"}
        </div>
      ),
    },
    {
      field: "referenceSource",
      headerName: "Reference Source",
      width: 130,
      renderCell: (params) => (
        <div className="text-capitalize">
          {params?.row?.referenceSource || "N/A"}
        </div>
      ),
    },
    {
      field: "assignedTo",
      headerName: "Caller",
      width: 130,
      renderCell: (params) => (
        <div className="text-capitalize">{params.row.assignedTo}</div>
      ),
    },

    { field: "email", headerName: "Email", width: 210 },
    {
      field: "probability",
      headerName: "Probability",
      width: 80,
      renderCell: (params) => (
        <div className="text-capitalize">
          {params?.row?.probability ? params?.row?.probability : "medium"}
        </div>
      ),
      sortable: true,
    },

    {
      field: "updatedAt",
      headerName: "Updated At",
      width: 210,
      valueFormatter: (params) =>
        params?.value ? convertToUTCString(params?.value) : "",
      filterOperators: customMuiDateFilter,
      renderCell: (params) => (
        <div>
          {params?.row?.lastActivityDate
            ? convertToUTCString(params?.row?.lastActivityDate)
            : convertToUTCString(params?.row?.createdTime)}
        </div>
      ),
    },
  ];

  return (
    <div style={{ width: "100%" }}>
      {selectedLeadIds.length > 0 && (
        <Box mb={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={openChangeOwnerDialog}
          >
            Change Lead Owner
          </Button>
        </Box>
      )}
      <DataGrid
        rows={processedRows}
        columns={columns.map((column) => ({
          ...column,
          headerClassName: "bg-light-400 ",
        }))}
        autoHeight
        hideFooter
        disableExtendRowFullWidth
        onStateChange={(state) => {
          const filteredRowIds = Object.entries(state.filter.filteredRowsLookup)
            .filter(([_, isVisible]) => isVisible === true)
            .map(([rowId]) => rowId); // Keep IDs as strings

          // Filter leadsData based on these IDs
          const filteredData = leadsData?.filter((row) =>
            filteredRowIds.includes(row?._id),
          );
          // Pass the filtered data to the parent
          // setFilteredData(filteredData);
        }}
        sx={{
          "& .sticky-column": {
            position: "sticky",
            right: 0,
            background: "white", // Prevents content from hiding under it
            zIndex: 100, // Keeps it on top
          },
          "& .MuiDataGrid-columnHeaders": {
            background: "white",
            position: "sticky",
            top: 0,
            zIndex: 110,
          },
          "& .MuiDataGrid-virtualScroller": {
            overflowX: "auto",
          },
        }}
        filterModel={filterModel}
        onFilterModelChange={(newModel) => dispatch(setFilterModel(newModel))}
        slots={{
          toolbar: GridToolbar, // Enable filter toolbar above the grid
        }}
        slotProps={{
          toolbar: {
            // showQuickFilter: true, // Show Quick Filter
            printOptions: { disableToolbarButton: true }, // Hide Print Button
            csvOptions: { disableToolbarButton: true }, // Hide CSV Export
          },
        }}
      />
      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Select New Owner</DialogTitle>
        <DialogContent dividers>
          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height={150}
            >
              <CircularProgress size={30} />
            </Box>
          ) : (
            <>
              <List className="text-capitalize">
                {users.map((user) => (
                  <ListItem
                    key={user._id}
                    className={`${
                      selectedUserId === user._id
                        ? "bg-primary text-white rounded-pill"
                        : ""
                    }`}
                    disablePadding
                    sx={{
                      mb: 1,
                      borderRadius: "8px",
                      "&.bg-primary": { color: "white" },
                    }}
                  >
                    <ListItemButton
                      onClick={() => setSelectedUserId(user._id)}
                      sx={{ paddingY: "6px", paddingX: "12px" }} // Reduce padding
                    >
                      <ListItemText primary={user.name} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>

              {hasMore && (
                <Box mt={2} textAlign="center">
                  <Button
                    variant="outlined"
                    onClick={() => setPage((prev) => prev + 1)}
                    color="primary"
                  >
                    Load More
                  </Button>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setIsDialogOpen(false);
              setPage(1);
              setHasMore(true);
              setSelectedUserId(null);
              setSelectedLeadIds([]);
            }}
            variant="outlined"
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={changeLeadOwner}
            disabled={!selectedUserId}
            variant="contained"
            color="primary"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <ShareLead
        open={shareDialogOpen}
        onClose={() => {
          setShareData({});
          setShareDialogOpen(false);
        }}
        shareData={shareData}
      />
      <PostCallModal
        isOpen={isOpenPostCallModal}
        openModal={openModal}
        closeModal={closeModal}
        leadId={selectedLeadId}
      />
    </div>
  );
};

export default LeadList;
